import { useEffect, useRef } from "react";
import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import {
  CRMConversationFilter,
  CRMConversationStatus,
} from "@jugl-web/rest-api/integrations/types";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { getChatSecondsToExpiration } from "../../ClientSupportChat/utils/getChatSecondsToExpiration";
import { getClientSupportPathParamsFromPathname } from "../../../utils";
import { useClientSupportPageContext } from "../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { isConversationContactBlocked } from "../../../utils/isConversationContactBlocked";

type UseSelectedConversationParams = {
  channel: CRMChannel;
  selectedFilter?: CRMConversationFilter;
  setSelectedFilter?: (value: CRMConversationFilter) => void;
};

export const useSelectedConversation = ({
  channel: hookChannel,
  selectedFilter,
  setSelectedFilter,
}: UseSelectedConversationParams) => {
  const { channel: urlChannel, activeConversation } =
    getClientSupportPathParamsFromPathname(window.location.pathname);
  const { selectedConversation, selectConversation } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const isInitializedRef = useRef<boolean>(false);

  if (selectedConversation && !isInitializedRef.current) {
    isInitializedRef.current = true;
  }

  const shouldFetch =
    !isInitializedRef.current &&
    !!activeConversation &&
    !!entityId &&
    hookChannel === urlChannel;

  const queryArgs = {
    entityId,
    convId: activeConversation ?? "",
  };

  const { data: whatsappConversation } =
    integrationsApi.useGetWhatsappConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.whatsapp,
    });

  const { data: instagramConversation } =
    integrationsApi.useGetInstagramConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.instagram,
    });

  const { data: webChatBotConversation } =
    integrationsApi.useGetWebChatBotConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.chatbot,
    });

  const { data: twilioConversation } =
    integrationsApi.useGetTwilioConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.twilio,
    });

  const { data: voiceConversation } =
    integrationsApi.useGetVoiceConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.voice,
    });

  const { data: emailConversation } =
    integrationsApi.useGetEmailConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.email,
    });

  const fetchedConversation =
    whatsappConversation ??
    instagramConversation ??
    webChatBotConversation ??
    twilioConversation ??
    voiceConversation ??
    emailConversation;

  useEffect(() => {
    if (isInitializedRef.current || !activeConversation || !fetchedConversation)
      return;

    if (setSelectedFilter) {
      let nextFilter = CRMConversationFilter.active;

      if (fetchedConversation.conv_status === CRMConversationStatus.closed) {
        nextFilter = CRMConversationFilter.closed;
      } else if (
        hookChannel === CRMChannel.whatsapp ||
        hookChannel === CRMChannel.instagram
      ) {
        if (isConversationContactBlocked(fetchedConversation)) {
          nextFilter = CRMConversationFilter.blocked;
        } else if (
          getChatSecondsToExpiration(
            "since" in fetchedConversation ? fetchedConversation.since : null
          ) <= 0
        ) {
          nextFilter = CRMConversationFilter.expired;
        }
      }

      if (nextFilter !== selectedFilter) {
        setSelectedFilter(nextFilter);
      }
    }

    selectConversation(fetchedConversation);
    isInitializedRef.current = true;
  }, [
    activeConversation,
    fetchedConversation,
    hookChannel,
    selectedFilter,
    selectConversation,
    setSelectedFilter,
  ]);
};
